import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BookOpen, GraduationCap, Sparkles, Library, MessageSquare, ArrowRight, Award, Layers } from "lucide-react";

const Feature = ({ icon: Icon, title, desc, testid }) => (
  <div data-testid={testid} className="kt-card p-6 hover:-translate-y-1 hover:shadow-lg transition">
    <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-[#0F2922] text-[#FAFA96] mb-4">
      <Icon size={22} />
    </div>
    <h3 className="text-lg font-semibold text-[#0F172A] mb-1">{title}</h3>
    <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
  </div>
);

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen kt-gradient-hero">
      <header className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <img src="https://customer-assets-v7afamib.emergentagent.net/job_thesis-mate/artifacts/l29gu6p6_Untitled%20design%20%283%29.png" alt="kawantugas.id" className="h-12 w-auto" />
        </Link>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => nav("/login")} data-testid="nav-login-button">Masuk</Button>
          <Button className="bg-[#0F2922] hover:bg-[#173E34] text-[#FAFA96]" onClick={() => nav("/register")} data-testid="nav-register-button">Daftar Gratis</Button>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 pt-10 pb-16 grid md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-7">
          <Badge className="bg-[#D97706] hover:bg-[#B45309] text-white uppercase tracking-widest text-[10px] font-semibold">Platform Akademik AI</Badge>
          <h1 className="serif text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-4 text-[#0F2922] leading-[1.05]">
            Kawan setia mahasiswa <span className="italic text-[#D97706]">S1, S2, S3</span> menyelesaikan tugas akademik.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-700 leading-relaxed max-w-2xl">
            Cari beasiswa, generate paper & slide presentasi dengan AI, telusuri direktori jurnal Scopus/Sinta, dan susun tugas akhir dari 5 judul sampai daftar pustaka — semua di satu tempat.
          </p>
          <div className="mt-7 flex gap-3">
            <Button size="lg" className="bg-[#0F2922] hover:bg-[#173E34] text-[#FAFA96] rounded-full px-6" onClick={() => nav("/register")} data-testid="hero-cta-register">
              Mulai Sekarang <ArrowRight size={18} className="ml-2" />
            </Button>
            <Button size="lg" variant="outline" className="rounded-full border-[#0F2922] text-[#0F2922]" onClick={() => nav("/login")} data-testid="hero-cta-login">Sudah punya akun</Button>
          </div>
          <div className="mt-8 grid grid-cols-3 gap-4 max-w-xl">
            <div className="kt-card p-4"><div className="text-2xl font-bold text-[#0F2922]">60+</div><div className="text-xs uppercase tracking-widest text-slate-500">Jurnal Terkurasi</div></div>
            <div className="kt-card p-4"><div className="text-2xl font-bold text-[#0F2922]">20+</div><div className="text-xs uppercase tracking-widest text-slate-500">Beasiswa Aktif</div></div>
            <div className="kt-card p-4"><div className="text-2xl font-bold text-[#0F2922]">AI</div><div className="text-xs uppercase tracking-widest text-slate-500">Paper + Slide</div></div>
          </div>
        </div>
        <div className="md:col-span-5">
          <div className="relative">
            <img src="https://images.unsplash.com/photo-1625111696958-792514071a8f?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Study" className="rounded-3xl shadow-xl aspect-[4/5] object-cover" />
            <div className="absolute -bottom-6 -left-6 kt-card p-4 flex items-center gap-3 shadow-lg">
              <Award className="text-[#D97706]" />
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Terintegrasi</div>
                <div className="font-semibold">Scopus · SINTA 2/3/4</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-14">
        <p className="text-xs uppercase tracking-widest text-[#D97706] font-semibold">Fitur Utama</p>
        <h2 className="serif text-3xl sm:text-4xl font-bold text-[#0F2922] mt-2 mb-8">Semua yang kamu butuhkan, satu akun.</h2>
        <div className="grid md:grid-cols-3 gap-5">
          <Feature icon={GraduationCap} title="Portal Beasiswa" desc="20+ beasiswa dalam & luar negeri untuk S1–S3 dengan deadline & syarat lengkap." testid="feature-beasiswa" />
          <Feature icon={Sparkles} title="AI Paper & Slide" desc="Draft paper multi-section + 10 slide presentasi otomatis. Export PDF/Word/PPT." testid="feature-paper" />
          <Feature icon={Library} title="Direktori Jurnal" desc="60+ jurnal Scopus Q1–Q4 & SINTA 2/3/4 lengkap dengan tombol submit langsung." testid="feature-jurnal" />
          <Feature icon={BookOpen} title="Ide Tugas Akhir" desc="Dapatkan 5 usulan judul + roadmap Bab 1–5 + daftar pustaka APA." testid="feature-ta" />
          <Feature icon={MessageSquare} title="Diskusi Antar Mahasiswa" desc="Komentar & tanya jawab di setiap beasiswa, jurnal, dan ide TA." testid="feature-diskusi" />
          <Feature icon={Layers} title="Multi-jenjang" desc="Kurikulum konten dipersonalisasi untuk S1, S2, dan S3." testid="feature-jenjang" />
        </div>
      </section>

      <footer className="border-t border-slate-200 mt-10 py-8 text-center text-sm text-slate-500">
        © 2026 kawantugas.id — Platform akademik untuk mahasiswa Indonesia.
      </footer>
    </div>
  );
}
